import React, { useState, useEffect } from "react";
import { Plus, Search, RefreshCw, ChevronDown, ChevronUp, MessageSquare, UserPlus, Send } from "lucide-react";
import { FaWrench, FaBolt, FaBug, FaPaintBrush, FaTools, FaCog } from "react-icons/fa";
import { MdElevator, MdPlumbing, MdElectricalServices } from "react-icons/md";
import Modal from './Modal'
import { API_BASE } from '../config'

async function apiFetch(path, opts = {}) {
  const token = sessionStorage.getItem("id_token");
  const resp = await fetch(`${API_BASE}${path}`, {
    ...opts,
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}`, ...opts.headers },
  });
  if (!resp.ok) throw new Error(`API ${resp.status}`);
  return resp.json();
}

const CATEGORIES = [
  { value: "plumbing", label: "Plumbing", icon: MdPlumbing },
  { value: "electrical", label: "Electrical", icon: MdElectricalServices },
  { value: "carpentry", label: "Carpentry", icon: FaTools },
  { value: "painting", label: "Painting", icon: FaPaintBrush },
  { value: "pest_control", label: "Pest Control", icon: FaBug },
  { value: "elevator", label: "Elevator", icon: MdElevator },
  { value: "general", label: "General", icon: FaCog },
  { value: "other", label: "Other", icon: FaWrench },
];

const PRIORITIES = [
  { value: "low", label: "Low", color: "bg-gray-100 text-gray-700" },
  { value: "medium", label: "Medium", color: "bg-yellow-100 text-yellow-700" },
  { value: "high", label: "High", color: "bg-orange-100 text-orange-700" },
  { value: "urgent", label: "Urgent", color: "bg-red-100 text-red-700" },
];

const STATUS_BADGES = {
  open: "bg-blue-100 text-blue-800",
  assigned: "bg-blue-100 text-blue-800",
  in_progress: "bg-yellow-100 text-yellow-800",
  resolved: "bg-green-100 text-green-800",
  closed: "bg-gray-100 text-gray-600",
  reopened: "bg-red-100 text-red-800",
};

const STATUS_OPTIONS = ["open", "assigned", "in_progress", "resolved", "closed", "reopened"];

// Suggested service staff roles by category (admin can type custom email)
const STAFF_SUGGESTIONS = {
  plumbing: "Plumber",
  electrical: "Electrician",
  carpentry: "Carpenter",
  painting: "Painter",
  pest_control: "Pest Control",
  elevator: "Elevator Tech",
  general: "Maintenance",
  other: "Maintenance",
};

function formatDate(val) {
  if (!val) return "";
  try {
    const d = new Date(val);
    return d.toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
  } catch { return val; }
}

function getCategoryIcon(cat) {
  const found = CATEGORIES.find(c => c.value === cat);
  if (found) {
    const Icon = found.icon;
    return <Icon size={16} />;
  }
  return <FaWrench size={16} />;
}

export default function AdminIssueManagement() {
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const [toasts, setToasts] = useState([]);

  // Assignment modal state
  const [assignModal, setAssignModal] = useState(null); // issue being assigned
  const [assignEmail, setAssignEmail] = useState("");
  const [assignName, setAssignName] = useState("");
  const [assigning, setAssigning] = useState(false);

  // Comment state
  const [commentIssueId, setCommentIssueId] = useState(null);
  const [commentText, setCommentText] = useState("");
  const [commenting, setCommenting] = useState(false);

  const communityId = sessionStorage.getItem("community_id") || "";
  const adminEmail = (() => {
    try {
      const t = sessionStorage.getItem("id_token");
      if (!t) return "";
      const p = JSON.parse(atob(t.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
      return (p.email || "").toLowerCase();
    } catch { return ""; }
  })();
  const adminName = (() => {
    try {
      const t = sessionStorage.getItem("id_token");
      if (!t) return "";
      const p = JSON.parse(atob(t.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
      return p.name || p.given_name || "Admin";
    } catch { return "Admin"; }
  })();

  function showToast(message, type = "success") {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => setToasts(prev => prev.filter(x => x.id !== id)), 3500);
  }

  async function loadIssues() {
    if (!communityId) return;
    setLoading(true);
    try {
      const data = await apiFetch(`/issues?communityId=${encodeURIComponent(communityId)}`);
      setIssues(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("loadIssues error:", err);
      showToast("Failed to load issues", "error");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadIssues(); }, [communityId]);

  async function handleStatusChange(issue, newStatus) {
    try {
      await apiFetch("/issues", {
        method: "PATCH",
        body: JSON.stringify({
          COMMUNITY_ID: communityId,
          ISSUE_ID: issue.ISSUE_ID,
          STATUS: newStatus,
        }),
      });
      showToast(`Status updated to ${newStatus.replace("_", " ")}`);
      loadIssues();
    } catch (err) {
      console.error("status change error:", err);
      showToast("Failed to update status", "error");
    }
  }

  async function handleAssign() {
    if (!assignEmail.trim()) {
      showToast("Please enter assignee email", "error");
      return;
    }
    setAssigning(true);
    try {
      await apiFetch("/issues", {
        method: "PATCH",
        body: JSON.stringify({
          COMMUNITY_ID: communityId,
          ISSUE_ID: assignModal.ISSUE_ID,
          ASSIGNED_TO: assignEmail.trim(),
          ASSIGNED_TO_NAME: assignName.trim() || assignEmail.trim().split("@")[0],
        }),
      });
      showToast(`Issue assigned to ${assignName || assignEmail}`);
      setAssignModal(null);
      setAssignEmail("");
      setAssignName("");
      loadIssues();
    } catch (err) {
      console.error("assign error:", err);
      showToast("Failed to assign issue", "error");
    } finally {
      setAssigning(false);
    }
  }

  async function handleComment(issueId) {
    if (!commentText.trim()) return;
    setCommenting(true);
    try {
      await apiFetch("/issues", {
        method: "PATCH",
        body: JSON.stringify({
          COMMUNITY_ID: communityId,
          ISSUE_ID: issueId,
          COMMENT: {
            AUTHOR: adminEmail,
            AUTHOR_NAME: adminName,
            TEXT: commentText.trim(),
          },
        }),
      });
      showToast("Comment added");
      setCommentText("");
      setCommentIssueId(null);
      loadIssues();
    } catch (err) {
      console.error("comment error:", err);
      showToast("Failed to add comment", "error");
    } finally {
      setCommenting(false);
    }
  }

  // Stats
  const statusCounts = {
    all: issues.length,
    open: issues.filter(i => i.STATUS === "open").length,
    assigned: issues.filter(i => i.STATUS === "assigned").length,
    in_progress: issues.filter(i => i.STATUS === "in_progress").length,
    resolved: issues.filter(i => i.STATUS === "resolved").length,
    closed: issues.filter(i => i.STATUS === "closed").length,
  };

  const filtered = issues.filter(issue => {
    const matchSearch = (issue.TITLE || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (issue.UNIT_NBR || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (issue.CATEGORY || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (issue.ASSIGNED_TO_NAME || "").toLowerCase().includes(searchTerm.toLowerCase());
    const matchStatus = filterStatus === "all" || (issue.STATUS || "") === filterStatus;
    return matchSearch && matchStatus;
  });

  return (
    <div className="p-4">
      {/* Toasts */}
      <div className="fixed top-4 right-4 z-[100] space-y-2">
        {toasts.map(t => (
          <div key={t.id} className={`px-4 py-2 rounded-lg shadow-lg text-white text-sm ${t.type === "error" ? "bg-red-500" : "bg-green-500"}`}>
            {t.message}
          </div>
        ))}
      </div>

      {/* Assign Modal */}
      {assignModal && (
        <Modal title="Assign Issue" onClose={() => setAssignModal(null)}>
          <p className="text-sm text-gray-500 mb-3">
            {assignModal.TITLE} — {STAFF_SUGGESTIONS[assignModal.CATEGORY] || "Staff"}
          </p>
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Staff Email *</label>
              <input
                type="email"
                value={assignEmail}
                onChange={e => setAssignEmail(e.target.value)}
                placeholder="plumber@gmail.com"
                className="w-full p-2 border border-gray-300 rounded-lg text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Staff Name</label>
              <input
                type="text"
                value={assignName}
                onChange={e => setAssignName(e.target.value)}
                placeholder="John the Plumber"
                className="w-full p-2 border border-gray-300 rounded-lg text-sm"
              />
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => setAssignModal(null)} className="px-4 py-2 text-sm text-gray-600 bg-gray-100 rounded-lg">
                Cancel
              </button>
              <button onClick={handleAssign} disabled={assigning} className="px-4 py-2 text-sm text-white bg-blue-600 rounded-lg disabled:opacity-50">
                {assigning ? "Assigning..." : "Assign"}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <div>
          <h2 className="text-lg font-bold">Issue Management</h2>
          <p className="text-xs">Track, assign, and manage community issues</p>
        </div>
        <button onClick={loadIssues} className="p-2 rounded-lg bg-gray-100 active:bg-gray-200">
          <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-2 mb-4">
        <div className="bg-blue-50 border border-blue-100 rounded-lg p-2 sm:p-3 text-center">
          <p className="text-lg sm:text-xl font-bold text-blue-700">{statusCounts.open}</p>
          <p className="text-[10px] text-blue-600">Open</p>
        </div>
        <div className="bg-yellow-50 border border-yellow-100 rounded-lg p-2 sm:p-3 text-center">
          <p className="text-lg sm:text-xl font-bold text-yellow-700">{statusCounts.in_progress + statusCounts.assigned}</p>
          <p className="text-[10px] text-yellow-600">In Progress</p>
        </div>
        <div className="bg-green-50 border border-green-100 rounded-lg p-2 sm:p-3 text-center">
          <p className="text-lg sm:text-xl font-bold text-green-700">{statusCounts.resolved + statusCounts.closed}</p>
          <p className="text-[10px] text-green-600">Resolved</p>
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-3">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          placeholder="Search by title, unit, category, assignee..."
          className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm"
        />
      </div>

      {/* Status Filter */}
      <div className="filter-pills pb-3">
        {[
          { key: "all", label: "All" },
          { key: "open", label: "Open" },
          { key: "assigned", label: "Assigned" },
          { key: "in_progress", label: "In Progress" },
          { key: "resolved", label: "Resolved" },
          { key: "closed", label: "Closed" },
        ].map(s => (
          <button
            key={s.key}
            onClick={() => setFilterStatus(s.key)}
            className={`filter-pill px-3 py-1 rounded-full text-xs font-medium ${
              filterStatus === s.key ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-600"
            }`}
          >
            {s.label} ({statusCounts[s.key] || 0})
          </button>
        ))}
      </div>

      {/* Issues List */}
      {loading && issues.length === 0 ? (
        <div className="text-center py-8 text-gray-400">Loading issues...</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-8 text-gray-400">
          <FaWrench size={28} className="mx-auto mb-2 text-gray-300" />
          <p className="text-center text-sm text-white py-8">No issues found</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(issue => {
            const isExpanded = expandedId === issue.ISSUE_ID;
            const comments = Array.isArray(issue.COMMENTS) ? issue.COMMENTS : [];
            const priorityConfig = PRIORITIES.find(p => p.value === issue.PRIORITY) || PRIORITIES[1];

            return (
              <div key={issue.ISSUE_ID} className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <button
                  className="w-full p-4 text-left"
                  onClick={() => setExpandedId(isExpanded ? null : issue.ISSUE_ID)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      <div className="mt-0.5 text-gray-500">{getCategoryIcon(issue.CATEGORY)}</div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{issue.TITLE}</p>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${STATUS_BADGES[issue.STATUS] || STATUS_BADGES.open}`}>
                            {(issue.STATUS || "open").replace("_", " ").toUpperCase()}
                          </span>
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${priorityConfig.color}`}>
                            {priorityConfig.label}
                          </span>
                          <span className="text-[10px] text-gray-400">Unit {issue.UNIT_NBR}</span>
                          {issue.ASSIGNED_TO_NAME && (
                            <span className="text-[10px] text-blue-600">→ {issue.ASSIGNED_TO_NAME}</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 ml-2">
                      {comments.length > 0 && (
                        <span className="flex items-center gap-1 text-gray-400 text-xs">
                          <MessageSquare size={12} /> {comments.length}
                        </span>
                      )}
                      {isExpanded ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
                    </div>
                  </div>
                </button>

                {isExpanded && (
                  <div className="border-t border-gray-100 px-4 pb-4">
                    {/* Details */}
                    {issue.DESCRIPTION && (
                      <div className="mt-3">
                        <p className="text-xs font-medium text-gray-500 mb-1">Description</p>
                        <p className="text-sm text-gray-700">{issue.DESCRIPTION}</p>
                      </div>
                    )}
                    <div className="grid grid-cols-2 gap-3 mt-3 text-xs">
                      <div>
                        <p className="text-gray-500">Reported By</p>
                        <p className="font-medium">{issue.REPORTED_BY_NAME || issue.REPORTED_BY}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Created</p>
                        <p className="font-medium">{formatDate(issue.CREATED_AT)}</p>
                      </div>
                      {issue.ASSIGNED_TO_NAME && (
                        <div>
                          <p className="text-gray-500">Assigned To</p>
                          <p className="font-medium">{issue.ASSIGNED_TO_NAME}</p>
                        </div>
                      )}
                      {issue.RESOLVED_AT && (
                        <div>
                          <p className="text-gray-500">Resolved</p>
                          <p className="font-medium">{formatDate(issue.RESOLVED_AT)}</p>
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="mt-3 flex flex-wrap gap-2">
                      {/* Assign Button */}
                      {(issue.STATUS === "open" || issue.STATUS === "reopened") && (
                        <button
                          onClick={(e) => { e.stopPropagation(); setAssignModal(issue); }}
                          className="flex items-center gap-1 px-3 py-1.5 text-xs bg-blue-50 text-blue-700 rounded-lg font-medium"
                        >
                          <UserPlus size={12} /> Assign
                        </button>
                      )}

                      {/* Status Change */}
                      <select
                        value={issue.STATUS}
                        onChange={(e) => handleStatusChange(issue, e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        className="text-xs px-2 py-1.5 border border-gray-200 rounded-lg bg-white"
                      >
                        {STATUS_OPTIONS.map(s => (
                          <option key={s} value={s}>{s.replace("_", " ").replace(/^\w/, c => c.toUpperCase())}</option>
                        ))}
                      </select>

                      {/* Re-assign if already assigned */}
                      {issue.ASSIGNED_TO && (
                        <button
                          onClick={(e) => { e.stopPropagation(); setAssignModal(issue); setAssignEmail(issue.ASSIGNED_TO || ""); setAssignName(issue.ASSIGNED_TO_NAME || ""); }}
                          className="flex items-center gap-1 px-3 py-1.5 text-xs bg-gray-50 text-gray-600 rounded-lg font-medium"
                        >
                          Re-assign
                        </button>
                      )}
                    </div>

                    {/* Comments */}
                    {comments.length > 0 && (
                      <div className="mt-3">
                        <p className="text-xs font-medium text-gray-500 mb-2">Comments ({comments.length})</p>
                        <div className="space-y-2">
                          {comments.map((cmt, idx) => (
                            <div key={cmt.COMMENT_ID || idx} className="bg-gray-50 rounded-lg p-2">
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-xs font-medium text-gray-700">{cmt.AUTHOR_NAME || cmt.AUTHOR || "Staff"}</span>
                                <span className="text-[10px] text-gray-400">{formatDate(cmt.CREATED_AT)}</span>
                              </div>
                              <p className="text-xs text-gray-600">{cmt.TEXT}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Add Comment */}
                    <div className="mt-3">
                      {commentIssueId === issue.ISSUE_ID ? (
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={commentText}
                            onChange={e => setCommentText(e.target.value)}
                            placeholder="Add a comment..."
                            className="flex-1 p-2 text-xs border border-gray-200 rounded-lg"
                            onClick={e => e.stopPropagation()}
                            onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); handleComment(issue.ISSUE_ID); } }}
                          />
                          <button
                            onClick={(e) => { e.stopPropagation(); handleComment(issue.ISSUE_ID); }}
                            disabled={commenting}
                            className="px-3 py-2 bg-blue-600 text-white rounded-lg disabled:opacity-50"
                          >
                            <Send size={14} />
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={(e) => { e.stopPropagation(); setCommentIssueId(issue.ISSUE_ID); }}
                          className="text-xs text-blue-600 font-medium"
                        >
                          + Add Comment
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
